import { CaretRight } from "@phosphor-icons/react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

import CampaignTypeIcon from "common/components/CampaignTypeIcon";
import { Button } from "common/components/ui/Button";
import { DialogFooter, DialogTitle } from "common/components/ui/Dialog";
import { DrawerFooter, DrawerTitle } from "common/components/ui/Drawer";
import Switcher from "common/components/ui/Switcher";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";
import { CAMPAIGN_TYPES } from "common/constants";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import { typedObjectEntries } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { CampaignOutreachType, CampaignType } from "common/types";

import type { Dispatch, SetStateAction } from "react";

interface CampaignTypesProps {
  type: CampaignType;
  setType: Dispatch<SetStateAction<CampaignType>>;
  outreachType: CampaignOutreachType;
  setOutreachType: Dispatch<SetStateAction<CampaignOutreachType>>;
  goToNextStep: () => void;
}

const switcherOptions = [
  {
    value: "connect",
    label: "Outbound",
  },
  {
    value: "drip",
    label: "Inbound",
  },
];

export default function CampaignTypes({
  type,
  setType,
  outreachType,
  setOutreachType,
  goToNextStep,
}: CampaignTypesProps) {
  const isDesktop = useTwBreakpoint("lg");
  const { accounts } = useSelectedWorkspaceContext();
  const hasLoggedInSalesNavigatorAccount = accounts.some(
    ({ sales_navigator, state }) => sales_navigator && state === "LoggedIn",
  );

  const isDripOutreach = outreachType === "drip";

  const { t } = useTranslation("campaigns", {
    keyPrefix: "campaignTypes",
  });

  const Footer = isDesktop ? DialogFooter : DrawerFooter;
  const Title = isDesktop ? DialogTitle : DrawerTitle;

  return (
    <>
      <Title className="mb-8">Create a new campaign</Title>

      <Switcher
        size="sm"
        options={switcherOptions}
        selectedOption={outreachType}
        onOptionSelect={(option) => {
          setOutreachType(option as CampaignOutreachType);

          if (type === "drip") {
            setType("search");
          }
        }}
        className="mx-auto w-[232px]"
      />

      <p className="my-3 text-center text-sm text-black-500">
        Target {isDripOutreach ? "1st" : "2nd & 3rd+"} connections
      </p>

      <div
        className={clsx(
          "flex flex-col lg:flex-row lg:flex-wrap gap-3 lg:mb-4 lg:grid-cols-2 overflow-y-auto scrollbar-thin",
          isDripOutreach && "max-lg:mb-20",
        )}
      >
        {typedObjectEntries(CAMPAIGN_TYPES).map(([key, { label }]) => {
          let disabledReason: string;
          if (key === "navigator") {
            disabledReason =
              !hasLoggedInSalesNavigatorAccount &&
              "You must have at least one logged in account with a Sales Navigator subscription to create a Sales Navigator campaign";
          }

          const isDisabled = !!disabledReason;
          const isSelected = type === key;

          const content = (
            <button
              type="button"
              key={key}
              onClick={() => setType(key)}
              className={clsx(
                "lg:basis-[calc(50%-6px)] group flex gap-x-2 rounded-3xl border p-3 transition-colors disabled:cursor-not-allowed disabled:border-black-200 disabled:bg-black-200",
                isSelected
                  ? `${
                      isDripOutreach
                        ? "border-blue-500 bg-blue-50"
                        : "border-purple-500 bg-purple-50"
                    }`
                  : "border-black-300 hover:bg-black-100",
                outreachType === "connect" && key === "drip" && "invisible",
              )}
              disabled={isDisabled}
            >
              <CampaignTypeIcon
                type={key}
                isDrip={isDripOutreach}
                className={clsx(
                  "transition-[background-color]",
                  isSelected && "bg-white",
                  isDisabled
                    ? "bg-black-100 opacity-60 [&_svg]:fill-black-950"
                    : "group-hover:bg-white",
                )}
              />
              <div className="flex flex-col gap-0.5 text-left">
                <h4 className="text-sm-bold group-disabled:text-black-400">
                  {label}
                </h4>

                <p className="text-xs-medium text-black-600 group-disabled:text-black-400">
                  {t(`${key}Description`)}
                </p>
              </div>
            </button>
          );

          if (isDisabled) {
            return (
              <Tooltip key={key}>
                <TooltipTrigger asChild>{content}</TooltipTrigger>
                <TooltipContent>{disabledReason}</TooltipContent>
              </Tooltip>
            );
          }

          return content;
        })}
      </div>

      <Footer>
        <Button
          rightIcon={<CaretRight />}
          className="ml-auto"
          onClick={goToNextStep}
        >
          Next
        </Button>
      </Footer>
    </>
  );
}
